import { useCallback, useEffect, useState } from 'react';

export const DRAFT_STORAGE_KEY = 'olymp-cohort-graph-draft-v1';
export const TOUR_STORAGE_KEY = 'olymp-cohort-graph-tour-seen-v1';

export const readDraftGraphState = ({ createEmptyGraphState, deserializeImportedGraph }) => {
  if (typeof window === 'undefined') {
    return createEmptyGraphState('Cohort Graph Draft');
  }

  try {
    const raw = window.localStorage.getItem(DRAFT_STORAGE_KEY);
    if (!raw) return createEmptyGraphState('Cohort Graph Draft');

    return deserializeImportedGraph(JSON.parse(raw));
  } catch {
    return createEmptyGraphState('Cohort Graph Draft');
  }
};

/**
 * Persists graph state to localStorage and manages first-time welcome visibility.
 */
export const useGraphPersistence = ({
  graphState,
  graphStateRef,
  serializeGraphForExport,
  showFeedback,
  persistRemoteNow,
}) => {
  const [showWelcomeTour, setShowWelcomeTour] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const payload = serializeGraphForExport(graphState);
      window.localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(payload));
    } catch {
      // Ignore draft save errors in local dev.
    }
  }, [graphState, serializeGraphForExport]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const alreadySeen = window.localStorage.getItem(TOUR_STORAGE_KEY);
    setShowWelcomeTour(!alreadySeen);
  }, []);

  const dismissWelcomeTour = useCallback(() => {
    setShowWelcomeTour(false);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(TOUR_STORAGE_KEY, '1');
    }
  }, []);

  const persistDraftNow = useCallback(async () => {
    if (typeof window === 'undefined') return;

    try {
      if (persistRemoteNow) {
        await persistRemoteNow(graphStateRef.current);
      }

      const payload = serializeGraphForExport(graphStateRef.current);
      window.localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(payload));
      showFeedback(
        persistRemoteNow ? 'Cohort saved to database.' : 'Draft saved to local storage.',
        'success',
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to save draft.';
      showFeedback(`Could not save draft: ${message}`, 'error');
    }
  }, [graphStateRef, persistRemoteNow, serializeGraphForExport, showFeedback]);

  return {
    showWelcomeTour,
    dismissWelcomeTour,
    persistDraftNow,
  };
};

export default useGraphPersistence;
